'use client';

import { PriceCardStoryblok } from '@/storyblok/types';
import clsx from 'clsx';
import { storyblokEditable } from '@storyblok/js';
import Link from 'next/link';
import { resolveWebLink } from '@/storyblok/utils/link';
import { blueCheck, greenCheck, roseCheck } from './checks';
import { StoryblokReactComponent } from '@/storyblok/components/componentsMap';

export const StoryblokPriceCard: StoryblokReactComponent<PriceCardStoryblok> = ({ component: card, story }) => {
  const cta = card.cta?.at(0);

  let bgHighlightClass = '';
  if (card.highlight) {
    if (card.color === 'green' || card.color === '') {
      bgHighlightClass = 'bg-[linear-gradient(90deg,_#0BBCA7_0%,_#5182EF_100%)]';
    } else if (card.color === 'blue') {
      bgHighlightClass = 'bg-[linear-gradient(90deg,_#5182EF_0%,_#759AEE_100%)]';
    } else if (card.color === 'rose') {
      bgHighlightClass = 'bg-[linear-gradient(90deg,_#E25292_0%,_#759AEE_100%)]';
    }
  } else {
    bgHighlightClass = 'bg-background-neutral';
  }

  return (
    <div className="w-full shrink-0 flex-grow lg:max-w-[33%]">
      <div
        {...storyblokEditable(card)}
        className={clsx(
          'relative flex h-full w-full rounded-xl shadow  transition-transform hover:-translate-y-1',
          card.highlight ? 'p-[3px]' : 'p-[1.5px]',
          bgHighlightClass,
        )}
      >
        {card.highlight && (
          <div
            className={clsx(
              'absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 rounded-full px-5 py-2 text-white',
              bgHighlightClass,
            )}
          >
            {card.highlight}
          </div>
        )}
        <div className="flex h-full w-full flex-col rounded-[10px] bg-white px-6 pb-4 pt-10">
          <h3
            className={clsx(
              'mb-4 text-2xl font-semibold',
              (card.color === '' || card.color === 'green') && 'text-brandGreen-400',
              card.color === 'blue' && 'text-brandBlue-500',
              card.color === 'rose' && 'text-pink-400',
            )}
          >
            {card.name}
          </h3>
          <div className="flex flex-row items-center gap-1">
            {card.discountedValue && (
              <div className="relative">
                <div
                  aria-hidden
                  className="pointer-events-none absolute bottom-2.5 left-0 h-[1.5px] w-full -rotate-12 bg-neutral-500 "
                ></div>
                <span className="text-sm font-medium text-neutral-lighter">{card.discountedValue}</span>
              </div>
            )}
            <span className="text-4xl font-semibold">{card.value}</span>
            <div className="flex h-full flex-grow flex-col justify-end">
              <span className="font-medium">{card.valueSuffix}</span>
            </div>
          </div>
          {card.description && <span>{card.description}</span>}
          <div className="h-8" />
          {card.content && (
            <>
              <p className="text-neutral-lighter">{card.content}</p>
              <div className="h-8" />
            </>
          )}

          {cta && (
            <Link
              className="w-full rounded-lg bg-primary py-4 text-center text-lg font-medium text-white transition-colors hover:bg-primary-hover"
              href={resolveWebLink(cta.link, { story })}
            >
              {cta.label}
            </Link>
          )}

          {card.bulletPoints && (
            <ul className="flex flex-col gap-5 py-8">
              {card.bulletPoints.map(point => (
                <li key={point._uid} {...storyblokEditable(point)} className="flex flex-row items-center justify-start gap-5">
                  <div className="shrink-0">
                    {(card.color === 'green' || card.color === '') && greenCheck}
                    {card.color === 'blue' && blueCheck}
                    {card.color === 'rose' && roseCheck}
                  </div>
                  <span>{point.content}</span>
                </li>
              ))}
              {card.bulletPointsEndText && <li className="text-neutral-lighter">{card.bulletPointsEndText}</li>}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
